/**
 * Hiring-demand board — one role, across the timeline.
 *
 * The row is also the DRAG HANDLE for moving a role into the right team. The
 * team a role sits in is derived (job-family map, server side) rather than
 * stored anywhere upstream, so it is a guess about a family — and this is where
 * the person who knows better corrects it for one seat. Dragging is the quick
 * path; the ⋯ picker beside it is the complete one, because an empty team has no
 * row to drop onto and a keyboard has no drag.
 */
import { Loader2, X } from 'lucide-react';
import type { BoardRole, DemandMonth, DraftInput, HireReason } from '../../../types/taDemand';
import { isDraft, isEditable } from '../../../types/taDemand';
import { cn } from '../../../../../shared';
import type { Track } from './demandModel';
import { hasBar, roleSpan, spanCovers, trackMonthKeys } from './demandModel';
import { ACTIONS, CELL, NameCells } from './demandGrid';
import { MoveTeamControl } from './TeamMove';
import { HireStateChip, HireWho } from './HireCell';
import {
  fmtFte,
  inputCls,
  numCls,
  parseNum,
  RoleMarker,
  RoleStateChip,
  selectCls,
  zoneClass,
} from './demandUi';
import { LEVELS } from './demandLevels';

const REASONS: { value: HireReason; label: string }[] = [
  { value: 'new', label: 'New hire' },
  { value: 'backfill', label: 'Backfill' },
];

export function RoleRow({
  role,
  tracks,
  months,
  currentMonth,
  depth,
  canApprove,
  busy,
  onPatch,
  onMove,
  onApprove,
  onDelete,
  teams,
  onMoveTeam,
  dragProps,
  dragging,
}: {
  role: BoardRole;
  tracks: Track[];
  months: DemandMonth[];
  currentMonth: string;
  depth: number;
  canApprove: boolean;
  busy: boolean;
  onPatch: (patch: Partial<DraftInput>) => void;
  onMove: (month: string) => void;
  onApprove: () => void;
  onDelete: () => void;
  /** Every team this role's unit has — what the move picker offers. */
  teams?: string[];
  onMoveTeam?: (team: string) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  dragging?: boolean;
}) {
  const editable = isEditable(role);
  const draft = isDraft(role);
  // The wait, if there is one: from the month the role was promised to the month
  // it landed, or to today while the seat is still empty.
  const span = roleSpan(role, months, currentMonth);
  const late = span.months > 0;
  // A bar is drawn whenever the role does not sit where it was promised — which
  // includes a plan already moved to a FUTURE month. That is a slip worth
  // seeing even though nobody is late for it yet.
  const drawBar = hasBar(span);
  const filled = !isDraft(role) && role.status === 'hired';

  return (
    <div
      {...dragProps}
      className={cn(
        'group grid min-h-[38px] items-stretch border-b border-gray-100',
        draft ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50',
        dragging && 'opacity-40',
        busy && 'opacity-60',
      )}
      style={{ gridTemplateColumns: 'var(--demand-tmpl)' }}
    >
      <NameCells
        depth={depth}
        bg={draft ? '!bg-blue-50' : 'bg-white'}
        className={`group-hover:bg-gray-50 ${
          draft ? 'shadow-[inset_2px_0_0_0_theme(colors.blue.500)]' : ''
        }`}
        who={
          editable ? (
            <input
              className={`${inputCls} min-w-0 flex-1 italic`}
              defaultValue={role.title}
              aria-label="Role title"
              /* The ROW is draggable, so without this Firefox reads a drag
                 inside the field as a drag of the row and the text cannot be
                 selected — the field looks broken rather than draggable. */
              draggable={false}
              onDragStart={(e) => e.stopPropagation()}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== role.title) onPatch({ title: v });
              }}
            />
          ) : (
            /* Black, against the grey of the people already in a seat — the
               roles are the work this board is about. */
            <span className="min-w-0 truncate text-[13px] font-medium text-gray-900" title={role.title}>
              {role.title}
            </span>
          )
        }
      >
        {/* 2 · level */}
        <div className={CELL}>
          {editable ? (
            <select
              className={selectCls}
              value={role.level || 'IC2'}
              aria-label="Level"
              draggable={false}
              onDragStart={(e) => e.stopPropagation()}
              onChange={(e) => onPatch({ level: e.target.value })}
            >
              {LEVELS.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          ) : (
            <span className="truncate tabular-nums text-gray-500">{role.level || '—'}</span>
          )}
        </div>

        {/* 3 · FTE. A row can carry more than one hire, so it is n × fte. */}
        <div className={`${CELL} tabular-nums text-gray-500`}>
          {editable ? (
            <>
              <input
                type="number"
                className={`${numCls} w-8 font-semibold`}
                defaultValue={role.count}
                min={1}
                max={20}
                step={1}
                aria-label="Number of hires"
                draggable={false}
                onDragStart={(e) => e.stopPropagation()}
                onBlur={(e) => {
                  const parsed = parseNum(e.target.value);
                  const n = Math.min(20, Math.max(1, Math.round(parsed ?? role.count)));
                  if (n !== role.count) onPatch({ count: n });
                }}
              />
              <span className="shrink-0 text-[11px] text-gray-400">×</span>
              <input
                type="number"
                className={`${numCls} w-11`}
                defaultValue={fmtFte(role.fte)}
                min={0.05}
                max={1}
                step={0.05}
                aria-label="FTE per hire"
                draggable={false}
                onDragStart={(e) => e.stopPropagation()}
                onBlur={(e) => {
                  const parsed = parseNum(e.target.value);
                  const v = Math.min(1, Math.max(0.05, parsed ?? role.fte));
                  if (v !== role.fte) onPatch({ fte: v });
                }}
              />
            </>
          ) : (
            <span className="truncate">
              {role.count > 1 && <b className="font-semibold text-gray-900">{role.count} × </b>}
              {fmtFte(role.fte)} FTE
            </span>
          )}
        </div>

        {/* 4 · who. New hire or backfill — and for a backfill, WHOSE seat. */}
        <div className={CELL}>
          {editable ? (
            <select
              className={`${selectCls} w-full`}
              value={role.reason === 'unknown' ? 'new' : role.reason}
              aria-label="Reason for hiring"
              draggable={false}
              onDragStart={(e) => e.stopPropagation()}
              onChange={(e) => onPatch({ reason: e.target.value as HireReason })}
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          ) : (
            /*
              THE NAME LIVES IN THE CHIP'S TOOLTIP, not beside it. Whose seat a
              backfill replaces is worth having and is not worth 170 pixels on
              every row — it was the widest column on the board and it pushed the
              role titles, which every row is actually about, into an ellipsis.
            */
            <HireWho role={role} />
          )}
        </div>

        {/* 5 · standing */}
        <div className={CELL}>
          <RoleStateChip role={role} />
          <HireStateChip role={role} />
          {!draft && role.viaPlanner && (
            <span
              className="shrink-0 rounded-sm border border-gray-200 px-1 text-[10px] leading-4 text-gray-400"
              title="Added in the planner, then approved into the HDB"
            >
              via planner
            </span>
          )}
        </div>

        {/* 6 · actions */}
        <div className={ACTIONS}>
          {onMoveTeam && (
            <MoveTeamControl role={role} teams={teams ?? []} busy={busy} onMove={onMoveTeam} />
          )}
          {editable && canApprove && (
            <button
              type="button"
              onClick={onApprove}
              disabled={busy}
              className="inline-flex h-[22px] shrink-0 items-center gap-1 rounded-sm bg-blue-600 px-2 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
              Approve
            </button>
          )}
          {editable && (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              aria-label={`Remove ${role.title}`}
              /*
                Visible WHILE BUSY, and spinning. Every mutation re-reads the
                whole board, so a delete takes a moment — and the button used to
                stay still and stay hover-only, so the row sat there unchanged
                and the click read as having done nothing.
              */
              /*
                `cn()` and not a template literal, and here it earns it: the two
                branches set CONFLICTING opacity utilities, and tailwind-merge is
                what guarantees the later one wins rather than leaving both in
                the class list for the cascade to settle.
              */
              className={cn(
                'grid h-[22px] w-[22px] shrink-0 place-items-center rounded-sm text-gray-400',
                'hover:bg-gray-100 hover:text-rose-600 focus:opacity-100',
                busy ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
              )}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin text-rose-600" /> : <X className="h-3 w-3" />}
            </button>
          )}
        </div>
      </NameCells>

      {tracks.map((t, i) => {
        const keys = trackMonthKeys(t);
        const here = role.month !== null && keys.includes(role.month);
        // Where the bar runs. A collapsed group counts as covered if ANY of its
        // months are, so a wait that began in the hidden past still shows.
        const covered = drawBar && keys.some((k) => spanCovers(span, k));
        const barStarts = drawBar && keys.some((k) => k === span.from);
        const barEnds = drawBar && !span.openEnded && keys.some((k) => k === span.to);
        const runsOffRight = covered && !barEnds && i === tracks.length - 1;

        const monthKey = t.kind === 'month' && !t.month.past ? t.month.key : null;
        const clickable = editable && monthKey !== null;

        const bar = covered ? (
          <span
            aria-hidden
            className={`pointer-events-none absolute top-1/2 h-[3px] -translate-y-1/2 ${
              // A wait that ENDED in a hire is green all the way, marker and
              // line: the story it tells is "this took nine months and we
              // closed it", not "something is wrong here".
              filled ? 'bg-emerald-500' : span.openEnded ? 'bg-rose-400' : late ? 'bg-amber-400' : 'bg-gray-300'
            } ${barStarts ? 'left-1/2 rounded-l-full' : 'left-0'} ${
              barEnds ? 'right-1/2 rounded-r-full' : 'right-0'
            }`}
          />
        ) : null;

        const content = (
          <>
            {bar}
            {/* An open-ended wait gets an arrowhead at the board's edge so it
                reads as "still running", not "ended here". */}
            {runsOffRight && span.openEnded && (
              <span
                aria-hidden
                className="pointer-events-none absolute right-0.5 top-1/2 -translate-y-1/2 text-[10px] font-bold leading-none text-rose-500"
              >
                ›
              </span>
            )}
            {here && <RoleMarker role={role} />}
            {/* The empty seat is marked at the month it was promised for. */}
            {!here && barStarts && span.openEnded && (
              <span
                className="relative grid h-5 min-w-5 place-items-center rounded-full border-2 border-dashed border-rose-400 px-1 text-[10px] font-bold tabular-nums text-rose-500"
                title={`Planned for ${span.from} — still not started`}
              >
                {role.count > 1 ? role.count : '·'}
              </span>
            )}
          </>
        );

        const title = span.openEnded && covered
          ? `Planned for ${span.from}, ${span.months} month${span.months === 1 ? '' : 's'} overdue`
          : draft
            ? undefined
            : 'Owned by the Hiring Database — change it there';

        return (
          <div
            key={i}
            className={`relative flex items-center justify-center ${zoneClass(t)}`}
          >
            {clickable ? (
              <button
                type="button"
                onClick={() => onMove(monthKey!)}
                title={`Move start to ${t.kind === 'month' ? t.month.label : ''} ${
                  t.kind === 'month' ? t.month.year : ''
                }`}
                className="relative grid h-full w-full place-items-center rounded-sm hover:bg-gray-100"
              >
                {content}
              </button>
            ) : (
              <span className="relative grid h-full w-full place-items-center" title={title}>
                {content}
              </span>
            )}
          </div>
        );
      })}

      {/*
        Nothing here any more. FTE, the count and the name a backfill replaces
        all moved into the frozen column, where they line up with the same
        values on every other row — a second copy out past the timeline was the
        duplication that made two of them disagree at a glance.
      */}
    </div>
  );
}

